import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { syncPests } from '../services/pestService';
import { DataTable, Field, Menu, MenuItem, Modal, Notice, StatusPill } from './patterns';
import type { DataTableColumn } from './patterns';

interface GlobalPest {
  _id: string;
  code: string;
  name: string;
}

interface Pest {
  _id: string;
  code: string;
  name: string;
  aliases: string[];
  order: number;
  reportOrder: number;
  excludeFromReport: boolean;
  measurementType: 'percentage' | 'level' | 'count';
  displayPreference: 'name' | 'alias';
  enabled: boolean;
  globalPestRef?: GlobalPest | null;
}

/**
 * Discriminated union for the unified DataTable rows. Local pest rows mix with
 * read-only "unlinked global pest" rows (Phase 3). Each kind has different
 * cell renderers and event handling.
 */
type PestRow =
  | { kind: 'pest'; pest: Pest }
  | { kind: 'globalUnlinked'; globalPest: GlobalPest };

/** Persisted key for order mode preference */
const ORDER_MODE_KEY = 'pestManager_orderMode';

const PestManager: React.FC = () => {
  const { t } = useTranslation();
  const [pests, setPests] = useState<Pest[]>([]);
  const [globalPests, setGlobalPests] = useState<GlobalPest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Phase 1: Inline editing — tracks per-pest dirty changes keyed by pest._id
  const [dirtyPests, setDirtyPests] = useState<Record<string, Partial<Pest>>>({});

  // Phase 2: Dual-mode ordering
  const [orderMode, setOrderMode] = useState<'field' | 'report'>(
    () => (localStorage.getItem(ORDER_MODE_KEY) as 'field' | 'report') || 'field'
  );

  // Phase 3: Selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isGlobalMode, setIsGlobalMode] = useState(false);
  const [currentPest, setCurrentPest] = useState<Partial<Pest>>({
    code: '',
    name: '',
    aliases: [],
    order: 0,
    reportOrder: 0,
    excludeFromReport: false,
    displayPreference: 'name',
    enabled: true,
  });

  const mockToken = 'citrus-synch-mock-token-123';
  const apiConfig = {
    headers: { Authorization: `Bearer ${mockToken}` },
  };

  const fetchPests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/pests', apiConfig);
      const sorted = response.data.sort((a: Pest, b: Pest) => (a.order ?? 0) - (b.order ?? 0));
      setPests(sorted);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch pests');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGlobalPests = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/global-pests', apiConfig);
      setGlobalPests(res.data);
    } catch {
      // Non-critical — degrade gracefully
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPests();
    fetchGlobalPests();
  }, [fetchPests, fetchGlobalPests]);

  /** Persist order mode to localStorage whenever it changes */
  useEffect(() => {
    localStorage.setItem(ORDER_MODE_KEY, orderMode);
  }, [orderMode]);

  /** Sorted pests based on current order mode */
  const sortedPests = [...pests].sort((a, b) => {
    if (orderMode === 'report') return (a.reportOrder ?? 0) - (b.reportOrder ?? 0);
    return (a.order ?? 0) - (b.order ?? 0);
  });

  /**
   * Global pests that do NOT yet have a local extension for this tenant.
   */
  const unlinkedGlobalPests = globalPests.filter(
    gp => !pests.some(p => p.globalPestRef?._id === gp._id)
  );

  const hasDirtyChanges = Object.keys(dirtyPests).length > 0;

  // ─── Inline Edit Helpers ────────────────────────────────────────────────────

  const getPestValue = <K extends keyof Pest>(pest: Pest, field: K): Pest[K] =>
    field in (dirtyPests[pest._id] || {}) ? (dirtyPests[pest._id] as any)[field] : (pest as any)[field];

  const markDirty = (pestId: string, changes: Partial<Pest>) => {
    setDirtyPests(prev => ({
      ...prev,
      [pestId]: { ...(prev[pestId] || {}), ...changes },
    }));
  };

  const handleInlineChange = (pest: Pest, field: keyof Pest, value: any) => {
    markDirty(pest._id, { [field]: value });
  };

  const handleBatchSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const updates = Object.entries(dirtyPests).map(([pestId, changes]) => {
        const original = pests.find(p => p._id === pestId)!;
        const merged = { ...original, ...changes };
        return axios.put(`/api/v1/pests/${pestId}`, merged, apiConfig);
      });
      await Promise.all(updates);
      await syncPests();
      setDirtyPests({});
      setSuccess(t('pestManager.saveChanges'));
      await fetchPests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  // ─── Modal Handlers ─────────────────────────────────────────────────────────

  const handleOpenModal = (pest?: Pest, globalMode = false) => {
    if (pest) {
      setIsEditing(true);
      setIsGlobalMode(globalMode);
      setCurrentPest({ ...pest });
    } else {
      setIsEditing(false);
      setIsGlobalMode(false);
      setCurrentPest({
        code: '',
        name: '',
        aliases: [],
        order: pests.length,
        reportOrder: 0,
        excludeFromReport: false,
        measurementType: 'percentage',
        displayPreference: 'name',
        enabled: true,
      });
    }
    setShowModal(true);
  };

  const handleCustomize = async (gp: GlobalPest) => {
    setLoading(true);
    try {
      const res = await axios.post(
        '/api/v1/pests',
        {
          code: gp.code,
          name: gp.name,
          globalPestRef: gp._id,
          aliases: (gp as any).aliases || [],
          measurementType: (gp as any).measurementType || 'percentage',
          displayPreference: 'name',
          order: pests.length,
        },
        apiConfig
      );
      await syncPests();
      await fetchPests();
      handleOpenModal({ ...res.data, globalPestRef: gp }, true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create pest extension');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | any>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'aliases') {
      setCurrentPest(prev => ({ ...prev, aliases: value.split(',').map((s: string) => s.trim()) }));
    } else if (type === 'checkbox') {
      setCurrentPest(prev => ({ ...prev, [name]: checked }));
    } else {
      setCurrentPest(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isEditing && currentPest._id) {
        await axios.put(`/api/v1/pests/${currentPest._id}`, currentPest, apiConfig);
      } else {
        await axios.post('/api/v1/pests', currentPest, apiConfig);
      }
      await syncPests();
      await fetchPests();
      handleCloseModal();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save pest');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('pestManager.deleteConfirm'))) return;
    setLoading(true);
    try {
      await axios.delete(`/api/v1/pests/${id}`, apiConfig);
      await syncPests();
      await fetchPests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete pest');
    } finally {
      setLoading(false);
    }
  };

  // ─── Drag & Drop ─────────────────────────────────────────────────────────────

  const updatePestOrder = async (pestList: Pest[]) => {
    setLoading(true);
    try {
      let updatedList: Pest[];
      if (orderMode === 'report') {
        updatedList = pestList.map((p, i) => ({ ...p, reportOrder: i }));
      } else {
        updatedList = pestList.map((p, i) => ({ ...p, order: i }));
      }
      await Promise.all(updatedList.map(pest =>
        axios.put(`/api/v1/pests/${pest._id}`, pest, apiConfig)
      ));
      await syncPests();
      setPests(updatedList);
    } catch {
      setError('Failed to update pest order');
      fetchPests();
    } finally {
      setLoading(false);
    }
  };

  const handleDefaultOrder = async () => {
    if (!window.confirm(t('pestManager.resetOrderConfirm'))) return;
    const sorted = [...pests].sort((a, b) => a.name.localeCompare(b.name));
    updatePestOrder(sorted);
  };

  const onDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const onDragOver = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const newList = [...sortedPests];
    const draggedItem = newList[draggedIndex];
    newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);
    // Re-number the active order field to match the new visual position so
    // the next render's sortedPests reflects the drag instead of resorting
    // back to the original order.
    const renumbered = newList.map((p, i) =>
      orderMode === 'report' ? { ...p, reportOrder: i } : { ...p, order: i }
    );
    setDraggedIndex(index);
    setPests(renumbered);
  };

  const onDragEnd = () => {
    setDraggedIndex(null);
    updatePestOrder(sortedPests);
  };

  // ─── Selection Mode & Bulk Actions ───────────────────────────────────────────

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedPests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedPests.map(p => p._id)));
    }
  };

  const handleBulkUpdate = async (field: keyof Pest, value: any) => {
    setLoading(true);
    setError(null);
    try {
      const updates = Array.from(selectedIds).map(id => {
        const pest = pests.find(p => p._id === id)!;
        const updated = { ...pest, [field]: value };
        return axios.put(`/api/v1/pests/${id}`, updated, apiConfig);
      });
      await Promise.all(updates);
      await syncPests();
      setSuccess(t('pestManager.bulkActions', { count: selectedIds.size }));
      setSelectedIds(new Set());
      await fetchPests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Bulk update failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── DataTable rows + columns ─────────────────────────────────────────────────

  // Pre-compute the union row list. Selection-mode select/all column is rendered
  // conditionally based on selectionMode at render time (column array updates).
  const tableRows: PestRow[] = [
    ...sortedPests.map((pest): PestRow => ({ kind: 'pest', pest })),
    ...unlinkedGlobalPests.map((gp): PestRow => ({ kind: 'globalUnlinked', globalPest: gp })),
  ];

  const pestColumns: DataTableColumn<PestRow>[] = [
    ...(selectionMode ? [{
      key: '__select',
      label: (
        <input
          type="checkbox"
          checked={selectedIds.size === sortedPests.length && sortedPests.length > 0}
          onChange={toggleSelectAll}
          aria-label="Select all pests"
        />
      ),
      render: (r: PestRow) => {
        if (r.kind !== 'pest') return null;
        const isSelected = selectedIds.has(r.pest._id);
        return (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(r.pest._id)}
            onClick={e => e.stopPropagation()}
            aria-label={`Select ${r.pest.name}`}
          />
        );
      },
    }] : []),
    {
      key: '__drag',
      label: '',
      render: (r: PestRow) => r.kind === 'pest'
        ? <span style={{ fontSize: '1.2rem', color: '#94a3b8' }}>☰</span>
        : null,
    },
    {
      key: 'code',
      label: t('pestManager.codeHeader'),
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') {
          return (
            <>
              <StatusPill variant="info">{t('pestManager.globalBadge')}</StatusPill>
              <code className="ms-2">{r.globalPest.code}</code>
            </>
          );
        }
        const isGlobal = !!r.pest.globalPestRef;
        return (
          <>
            {isGlobal && (
              <StatusPill variant="info">{t('pestManager.globalBadge')}</StatusPill>
            )}
            <code className={isGlobal ? 'ms-2' : ''}>{r.pest.code}</code>
          </>
        );
      },
    },
    {
      key: 'name',
      label: t('pestManager.nameHeader'),
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') {
          return <span className="text-muted fst-italic">{r.globalPest.name}</span>;
        }
        const pest = r.pest;
        const isGlobal = !!pest.globalPestRef;
        const currentName = getPestValue(pest, 'name') as string;
        return (
          <input
            type="text"
            value={currentName}
            disabled={isGlobal}
            onChange={e => handleInlineChange(pest, 'name', e.target.value)}
            aria-label={`Name for ${pest.code}`}
            style={{ minWidth: '120px' }}
          />
        );
      },
    },
    {
      key: 'aliases',
      label: t('pestManager.aliasesHeader'),
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') {
          return <span className="text-muted fst-italic">—</span>;
        }
        const pest = r.pest;
        const currentAliases = getPestValue(pest, 'aliases') as string[];
        return (
          <input
            type="text"
            value={currentAliases?.join(', ') || ''}
            onChange={e =>
              handleInlineChange(
                pest,
                'aliases',
                e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean)
              )
            }
            aria-label={`Aliases for ${pest.code}`}
            style={{ minWidth: '120px' }}
          />
        );
      },
    },
    {
      key: 'displayPref',
      label: t('pestManager.displayPrefHeader'),
      align: 'center',
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') {
          return <StatusPill variant="info">Name</StatusPill>;
        }
        const pest = r.pest;
        const currentDisplayPref = getPestValue(pest, 'displayPreference') as string;
        return (
          <button
            type="button"
            className="pest-manager-display-toggle"
            onClick={e => {
              e.stopPropagation();
              handleInlineChange(
                pest,
                'displayPreference',
                currentDisplayPref === 'alias' ? 'name' : 'alias'
              );
            }}
            aria-label="Toggle display preference"
            title="Click to toggle between Name and Alias"
          >
            <StatusPill variant={currentDisplayPref === 'alias' ? 'info' : 'ok'}>
              {currentDisplayPref === 'alias' ? 'Alias' : 'Name'}
            </StatusPill>
          </button>
        );
      },
    },
    {
      key: 'report',
      label: t('pestManager.reportHeader'),
      align: 'center',
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') return '—';
        const pest = r.pest;
        const currentExclude = getPestValue(pest, 'excludeFromReport') as boolean;
        return (
          <input
            type="checkbox"
            checked={!currentExclude}
            onChange={e => {
              e.stopPropagation();
              handleInlineChange(pest, 'excludeFromReport', !e.target.checked);
            }}
            aria-label={`Report for ${pest.name}`}
          />
        );
      },
    },
    {
      key: 'measurementType',
      label: t('pestManager.measurementType'),
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') return '—';
        const pest = r.pest;
        const currentMeasurement = getPestValue(pest, 'measurementType') as string;
        return (
          <select
            value={currentMeasurement || 'percentage'}
            onChange={e => {
              e.stopPropagation();
              handleInlineChange(pest, 'measurementType', e.target.value);
            }}
            style={{ minWidth: '110px' }}
            aria-label={`Measurement type for ${pest.name}`}
          >
            <option value="percentage">{t('pestManager.percentage')}</option>
            <option value="level">{t('pestManager.level')}</option>
            <option value="count">{t('pestManager.count')}</option>
          </select>
        );
      },
    },
    {
      key: 'enabled',
      label: t('pestManager.enabledHeader'),
      align: 'center',
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') return '—';
        const pest = r.pest;
        const currentEnabled = getPestValue(pest, 'enabled') as boolean;
        return (
          <input
            type="checkbox"
            checked={currentEnabled !== false}
            onChange={e => {
              e.stopPropagation();
              handleInlineChange(pest, 'enabled', e.target.checked);
            }}
            aria-label={`Enable ${pest.name}`}
          />
        );
      },
    },
    {
      key: 'actions',
      label: t('pestManager.actionsHeader'),
      render: (r: PestRow) => {
        if (r.kind === 'globalUnlinked') {
          return (
            <button
              type="button"
              className="pat-btn pat-btn--outline"
              onClick={() => handleCustomize(r.globalPest)}
              aria-label={t('pestManager.customize')}
            >
              {t('pestManager.customize')}
            </button>
          );
        }
        const pest = r.pest;
        const isGlobal = !!pest.globalPestRef;
        return (
          <>
            <button
              type="button"
              className="pat-btn pat-btn--subtle me-2"
              onClick={e => { e.stopPropagation(); handleOpenModal(pest, isGlobal); }}
              aria-label={t('pestManager.edit')}
            >
              {t('pestManager.edit')}
            </button>
            {!isGlobal && (
              <button
                type="button"
                className="pat-btn pat-btn--danger"
                onClick={e => { e.stopPropagation(); handleDelete(pest._id); }}
                aria-label={t('pestManager.delete')}
              >
                {t('pestManager.delete')}
              </button>
            )}
          </>
        );
      },
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('pestManager.heading')}</h2>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={handleDefaultOrder}
            disabled={loading || pests.length === 0}
          >
            {t('pestManager.defaultOrder')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => handleOpenModal()}
          >
            {t('pestManager.addNew')}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Notice tone="danger" className="mb-3" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setError(null)} aria-label="Close">×</button>}>
          <span>{error}</span>
        </Notice>
      )}
      {success && (
        <Notice tone="success" role="alert" className="mb-3" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setSuccess(null)} aria-label="Close">×</button>}>
          <span>{success}</span>
        </Notice>
      )}

      {/* Controls bar */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        {/* Phase 2: Order Mode Toggle */}
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold text-muted" style={{ fontSize: '0.875rem' }}>{t('pestManager.orderMode')}</span>
          <div role="group" aria-label={t('pestManager.orderMode')} className="pest-manager-order-toggle">
            <button
              type="button"
              className={`pat-btn ${orderMode === 'field' ? 'pat-btn--primary' : 'pat-btn--outline'}`}
              onClick={() => setOrderMode('field')}
              aria-pressed={orderMode === 'field'}
            >
              {t('pestManager.field')}
            </button>
            <button
              type="button"
              className={`pat-btn ${orderMode === 'report' ? 'pat-btn--primary' : 'pat-btn--outline'}`}
              onClick={() => setOrderMode('report')}
              aria-pressed={orderMode === 'report'}
            >
              {t('pestManager.reporting')}
            </button>
          </div>
        </div>

        {/* Phase 3: Selection Mode Toggle */}
        <button
          type="button"
          className={`pat-btn ${selectionMode ? 'pat-btn--primary' : 'pat-btn--subtle'}`}
          onClick={toggleSelectionMode}
          aria-pressed={selectionMode}
        >
          {selectionMode ? t('pestManager.exitSelection') : t('pestManager.selectionMode')}
        </button>

        {/* Phase 3: Bulk Actions Menu */}
        {selectionMode && selectedIds.size > 0 && (
          <Menu
            trigger={
              <button type="button" className="pat-btn pat-btn--primary">
                {t('pestManager.bulkActions', { count: selectedIds.size })}
              </button>
            }
          >
            <div className="pest-manager-menu-header">{t('pestManager.displayPreference')}</div>
            <MenuItem onClick={() => handleBulkUpdate('displayPreference', 'name')}>
              {t('pestManager.setToName')}
            </MenuItem>
            <MenuItem onClick={() => handleBulkUpdate('displayPreference', 'alias')}>
              {t('pestManager.setToAlias')}
            </MenuItem>
            <hr className="pest-manager-menu-divider" />
            <div className="pest-manager-menu-header">{t('pestManager.measurementType')}</div>
            <MenuItem onClick={() => handleBulkUpdate('measurementType', 'percentage')}>
              {t('pestManager.setToPercentage')}
            </MenuItem>
            <MenuItem onClick={() => handleBulkUpdate('measurementType', 'level')}>
              {t('pestManager.setToLevel')}
            </MenuItem>
            <MenuItem onClick={() => handleBulkUpdate('measurementType', 'count')}>
              {t('pestManager.setToCount')}
            </MenuItem>
            <hr className="pest-manager-menu-divider" />
            <div className="pest-manager-menu-header">{t('pestManager.enabledStatus')}</div>
            <MenuItem onClick={() => handleBulkUpdate('enabled', true)}>
              {t('pestManager.enableAll')}
            </MenuItem>
            <MenuItem onClick={() => handleBulkUpdate('enabled', false)}>
              {t('pestManager.disableAll')}
            </MenuItem>
          </Menu>
        )}

        {/* Phase 1: Batch Save */}
        <button
          type="button"
          className="pat-btn pat-btn--primary ms-auto"
          disabled={!hasDirtyChanges || loading}
          onClick={handleBatchSave}
        >
          {t('pestManager.saveChanges')}
        </button>
      </div>

      {/* Table — pest rows + read-only unlinked global pest rows */}
      <DataTable<PestRow>
        columns={pestColumns}
        rows={tableRows}
        rowKey={(r) => r.kind === 'pest' ? r.pest._id : `g-${r.globalPest._id}`}
        rowClassName={(r) => {
          if (r.kind === 'globalUnlinked') return 'table-light';
          const idx = sortedPests.findIndex(p => p._id === r.pest._id);
          const isDirty = !!dirtyPests[r.pest._id];
          const isSelected = selectedIds.has(r.pest._id);
          return [
            draggedIndex === idx ? 'table-active' : '',
            isDirty ? 'table-warning' : '',
            isSelected ? 'table-info' : '',
          ].filter(Boolean).join(' ');
        }}
        rowProps={(r) => {
          if (r.kind !== 'pest') return {};
          const idx = sortedPests.findIndex(p => p._id === r.pest._id);
          return {
            draggable: !selectionMode,
            onDragStart: !selectionMode ? (e) => onDragStart(e, idx) : undefined,
            onDragOver: !selectionMode ? (e) => { e.preventDefault(); onDragOver(idx); } : undefined,
            onDragEnd: !selectionMode ? onDragEnd : undefined,
            onClick: selectionMode ? () => toggleSelect(r.pest._id) : undefined,
            style: {
              cursor: selectionMode ? 'pointer' : 'move',
              opacity: draggedIndex === idx ? 0.5 : 1,
            },
          };
        }}
        emptyState={<div className="text-center py-3">{t('pestManager.noPests')}</div>}
      />

      {/* Edit / Add Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        title={
          isEditing
            ? isGlobalMode ? t('pestManager.customizeTitle') : t('pestManager.editTitle')
            : t('pestManager.addTitle')
        }
      >
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Notice tone="danger" className="mb-3" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setError(null)} aria-label="Close">×</button>}>
                <span>{error}</span>
              </Notice>
            )}

            <Field
              label={t('pestManager.pestCode')}
              controlId="pestCode"
              required
              helpText={
                isGlobalMode ? t('pestManager.codeReadOnly')
                : isEditing ? <strong className="text-warning">{t('pestManager.codeWarning')}</strong>
                : undefined
              }
            >
              <input
                id="pestCode"
                type="text"
                name="code"
                value={currentPest.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
                  setCurrentPest(prev => ({ ...prev, code: val }));
                }}
                required
                disabled={isEditing}
                placeholder={t('pestManager.pestCodePlaceholder')}
                aria-label={t('pestManager.pestCode')}
              />
            </Field>

            <Field
              label={t('pestManager.pestName')}
              controlId="pestName"
              required
              helpText={isGlobalMode ? t('pestManager.nameReadOnly') : undefined}
            >
              <input
                id="pestName"
                type="text"
                name="name"
                value={currentPest.name}
                onChange={handleChange}
                required
                disabled={isGlobalMode}
                placeholder={t('pestManager.pestNamePlaceholder')}
                aria-label={t('pestManager.pestName')}
              />
            </Field>

            <Field label={t('pestManager.aliases')} controlId="pestAliases">
              <input
                id="pestAliases"
                type="text"
                name="aliases"
                value={currentPest.aliases?.join(', ')}
                onChange={handleChange}
                placeholder={t('pestManager.aliasesPlaceholder')}
                aria-label={t('pestManager.aliases')}
              />
            </Field>

            <Field
              label={t('pestManager.displayPref')}
              controlId="pestDisplayPreference"
              helpText={t('pestManager.displayPrefHelp')}
            >
              <select
                id="pestDisplayPreference"
                name="displayPreference"
                value={currentPest.displayPreference || 'name'}
                onChange={handleChange}
                aria-label={t('pestManager.displayPref')}
              >
                <option value="name">{t('pestManager.pestNameOption')}</option>
                <option value="alias">{t('pestManager.aliasOption')}</option>
              </select>
            </Field>

            <Field label={t('pestManager.reportOrder')} controlId="pestReportOrder">
              <input
                id="pestReportOrder"
                type="number"
                name="reportOrder"
                value={currentPest.reportOrder ?? 0}
                onChange={handleChange}
                placeholder={t('pestManager.reportOrderPlaceholder')}
              />
            </Field>

            <Field label={t('pestManager.measurementType')} controlId="pestMeasurementType">
              <select
                id="pestMeasurementType"
                name="measurementType"
                value={currentPest.measurementType || 'percentage'}
                onChange={handleChange}
                aria-label={t('pestManager.measurementType')}
              >
                <option value="percentage">{t('pestManager.percentage')}</option>
                <option value="level">{t('pestManager.level')}</option>
                <option value="count">{t('pestManager.count')}</option>
              </select>
            </Field>

            <div className="pat-field pest-manager-checkbox-field">
              <label className="pat-checkbox-label">
                <input
                  type="checkbox"
                  name="excludeFromReport"
                  checked={currentPest.excludeFromReport || false}
                  onChange={handleChange}
                />
                <span>{t('pestManager.excludeFromReport')}</span>
              </label>
              <div className="pat-field__help">{t('pestManager.excludeHelp')}</div>
            </div>

            <div className="pat-field pest-manager-checkbox-field">
              <label className="pat-checkbox-label">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={currentPest.enabled !== false}
                  onChange={handleChange}
                />
                <span>{t('pestManager.enabled')}</span>
              </label>
              <div className="pat-field__help">{t('pestManager.enabledHelp')}</div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleCloseModal}
            >
              {t('pestManager.cancel')}
            </button>
            <button
              type="submit"
              className="pat-btn pat-btn--primary"
              disabled={loading}
            >
              {isEditing ? t('pestManager.update') : t('pestManager.save')}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default PestManager;
